import type { MessageRef, ShortlistItemType } from '@elara/domain/models/schemas/shortlist-schemas';

import { useMemo, useState } from 'react';

import { cn, metadataForIdentity } from '@elara/client-core/lib/utils';

import { useShortlistState, useTripsState } from '#states/hooks.js';

import { Button } from './button.js';
import { useCompactGuideActions } from './guides/guide-header-actions.js';
import { useItemAvailability } from './hooks/useItemAvailability.js';
import { MaterialIcon } from './MaterialIcon.js';
import { SelectGroupsDialog } from './shortlist/SelectGroupsDialog.js';

export interface ShortlistButtonProps {
  /** The type of item being shortlisted */
  itemType: ShortlistItemType;
  /** The reference ID of the item (e.g., hotelId, destinationId, roomOptionId) */
  referenceId: string;
  /** The title to display for the shortlisted item */
  title: string;
  /** Optional reference to the message where this recommendation was made */
  messageRef?: MessageRef;
  /** Optional metadata saved with the item (room: `{ occupancy }`, the party to price the saved room for, and optionally a pinned `productCode`). */
  metadata?: Record<string, unknown>;
  /** Additional class name */
  className?: string;
  /** Variant of the button */
  variant?: 'default' | 'icon';
  /** Icon-variant button size. Smaller sizes suit a card cover, where a 40px target crowds the photo. */
  size?: 'icon' | 'icon-sm' | 'icon-xs';
}

export function ShortlistButton({
  itemType,
  referenceId,
  title,
  messageRef,
  metadata,
  className,
  variant: variantProp,
  size: sizeProp,
}: ShortlistButtonProps) {
  // In a guide panel's floating header (`guide-header-actions`) the button takes its icon-only form.
  const compact = useCompactGuideActions();
  const variant = variantProp ?? (compact ? 'icon' : 'default');
  const size = sizeProp ?? (compact ? 'icon-sm' : 'icon');
  const shortlistState = useShortlistState();
  const tripsState = useTripsState();

  const [isHovered, setIsHovered] = useState(false);
  const [selectGroupsOpen, setSelectGroupsOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'remove'>('add');
  const [itemsInGroups, setItemsInGroups] = useState<Array<{ itemId: string; groupId: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const identityMeta = useMemo(() => metadataForIdentity(itemType, metadata), [itemType, metadata]);
  const isShortlisted = shortlistState.isShortlisted(itemType, referenceId, identityMeta);
  const hasTrip = !!tripsState.current;
  const groups = shortlistState.groups;
  const hasMultipleGroups = groups.length > 1;

  // Get all instances of this item (in case it's in multiple groups); an unpinned room matches every entry for it.
  const allItemInstances = shortlistState.getAllByReference(itemType, referenceId, identityMeta);
  const isInMultipleGroups = allItemInstances.length > 1;

  // Show dialog if: adding with multiple groups, or removing from multiple groups
  const shouldShowDialog = (isShortlisted && isInMultipleGroups) || (!isShortlisted && hasMultipleGroups);

  // Memoize group IDs for remove mode to prevent infinite loops
  const removeModeGroupIds = useMemo(() => {
    if (dialogMode === 'remove' && itemsInGroups.length > 0) {
      return itemsInGroups.map((item) => item.groupId);
    }
    return undefined;
  }, [dialogMode, itemsInGroups]);

  const isUnavailable = useItemAvailability(itemType, referenceId, metadata);
  const isDisabled = isLoading || !hasTrip || (isUnavailable && !isShortlisted);

  const icon = isShortlisted ? (isHovered ? 'heart_minus' : 'favorite') : isHovered ? 'heart_plus' : 'favorite';

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasTrip) return;

    // Don't set loading state when opening dialogs
    if (isShortlisted) {
      if (isInMultipleGroups) {
        // Show dialog to select which groups to remove from
        const itemsWithGroups = allItemInstances
          .filter((item) => item.groupId)
          .map((item) => ({ itemId: item.id, groupId: item.groupId! }));
        setItemsInGroups(itemsWithGroups);
        setDialogMode('remove');
        setSelectGroupsOpen(true);
        return; // No loading state needed for dialog
      }
    }

    if (!isShortlisted && hasMultipleGroups) {
      setDialogMode('add');
      setSelectGroupsOpen(true);
      return; // No loading state needed for dialog
    }

    // Only set loading state for actual async operations
    setIsLoading(true);
    try {
      if (isShortlisted) {
        const existing = allItemInstances[0];
        if (existing) {
          await shortlistState.removeItem(existing.id);
        }
      } else {
        await shortlistState.toggleItem(itemType, referenceId, { title, messageRef, metadata });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmGroups = async (groupIds: string[]) => {
    setIsLoading(true);
    try {
      if (dialogMode === 'add') {
        await shortlistState.addItemToGroups({ type: itemType, title, messageRef, metadata, referenceId }, groupIds);
      } else {
        // Remove mode: remove items from selected groups
        const itemIdsToRemove = itemsInGroups
          .filter((item) => groupIds.includes(item.groupId))
          .map((item) => item.itemId);
        if (itemIdsToRemove.length > 0) {
          await shortlistState.removeItemsFromGroups(itemIdsToRemove);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === 'icon') {
    return (
      <>
        <Button
          variant="icon"
          size={size}
          disabled={isDisabled}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          tooltip={isDisabled ? undefined : isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
          className={cn(
            'hover:bg-destructive/10 hover:text-destructive',
            isShortlisted && 'text-destructive',
            className,
          )}
        >
          <MaterialIcon icon={icon} filled={isShortlisted} />
        </Button>
        {shouldShowDialog && (
          <SelectGroupsDialog
            open={selectGroupsOpen}
            onOpenChange={setSelectGroupsOpen}
            onConfirm={handleConfirmGroups}
            mode={dialogMode}
            title={dialogMode === 'add' ? 'Add to shortlists' : 'Remove from shortlists'}
            description={
              dialogMode === 'add'
                ? `Select one or more shortlists to add "${title}" to:`
                : `Select which shortlists to remove "${title}" from:`
            }
            preselectedGroupIds={removeModeGroupIds}
            groupsToShow={removeModeGroupIds}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        disabled={isDisabled}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn(
          'group hover:bg-destructive hover:text-destructive-foreground hover:border-transparent',
          isShortlisted && 'text-destructive',
          className,
        )}
        tooltip={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
      >
        <MaterialIcon icon={icon} filled={isShortlisted} />
        {isShortlisted ? 'Shortlisted' : 'Shortlist'}
      </Button>
      {shouldShowDialog && (
        <SelectGroupsDialog
          open={selectGroupsOpen}
          onOpenChange={setSelectGroupsOpen}
          onConfirm={handleConfirmGroups}
          mode={dialogMode}
          title={dialogMode === 'add' ? 'Add to shortlists' : 'Remove from shortlists'}
          description={
            dialogMode === 'add'
              ? `Select one or more shortlists to add "${title}" to:`
              : `Select which shortlists to remove "${title}" from:`
          }
          preselectedGroupIds={dialogMode === 'remove' ? itemsInGroups.map((item) => item.groupId) : undefined}
          groupsToShow={dialogMode === 'remove' ? itemsInGroups.map((item) => item.groupId) : undefined}
        />
      )}
    </>
  );
}
